/**
 * Auth utility functions for managing authentication tokens.
 *
 * These utilities provide a centralized way to manage auth tokens
 * used by the API client for Authorization headers.
 */

/** Storage key for auth token - must match client.ts */
const AUTH_TOKEN_KEY = 'auth_token';

/** Shell login URL for authentication redirect */
export const SHELL_LOGIN_URL = 'https://platform.apxstream.com/auth/login';

/**
 * Store an authentication token in localStorage.
 * The token will be automatically included in API requests via the axios interceptor.
 *
 * @param token - The JWT or bearer token to store
 */
export function setAuthToken(token: string): void {
  localStorage.setItem(AUTH_TOKEN_KEY, token);
}

/**
 * Remove the authentication token from localStorage.
 * Call this on logout or when the token is invalidated.
 */
export function clearAuthToken(): void {
  localStorage.removeItem(AUTH_TOKEN_KEY);
}

/**
 * Retrieve the current authentication token from localStorage.
 *
 * @returns The stored token or null if not set
 */
export function getAuthToken(): string | null {
  return localStorage.getItem(AUTH_TOKEN_KEY);
}

/**
 * Check if an authentication token is currently stored.
 *
 * @returns true if a token exists, false otherwise
 */
export function hasAuthToken(): boolean {
  return getAuthToken() !== null;
}

/**
 * Get API base URL (mirrors client.ts logic).
 * Duplicated here to allow raw fetch calls that bypass the axios 401 interceptor.
 */
function getApiBaseUrl(): string {
  const runtimeUrl = (window as Window & { __ENV__?: { VITE_API_BASE_URL?: string } }).__ENV__
    ?.VITE_API_BASE_URL;
  if (runtimeUrl) return runtimeUrl;
  return import.meta.env.VITE_API_BASE_URL || '';
}

/**
 * Exchange Shell cookie for JWT token via GET /auth/me.
 *
 * Uses raw fetch (not axios) to avoid triggering the 401 interceptor.
 * `credentials: 'include'` sends .apxstream.com cookies cross-origin.
 */
export async function fetchAuthMe(): Promise<{ token: string; firm_id: number; user_id: string }> {
  const baseUrl = getApiBaseUrl();
  const res = await fetch(`${baseUrl}/v1/insight/auth/me`, {
    credentials: 'include',
  });
  if (!res.ok) {
    if (res.status === 401) throw new Error('Not authenticated');
    const detail = await res.text().catch(() => '');
    throw new Error(`Auth failed (${res.status}): ${detail || res.statusText}`);
  }
  return res.json();
}
